/**
 * Sequential Year → Make → Model selection.
 *
 * Year is a static range (newest+1 → 1950), Make is the curated picklist,
 * and Model options load live from NHTSA vPIC (same free, CORS-enabled US
 * DOT API the VIN lookup uses — no JSONP/third-party scripts):
 *   GetModelsForMakeYear/make/{makeName}/modelyear/{year}?format=json
 *
 * Make is disabled until a year is chosen; Model until a make is chosen.
 * The model list always ends with a "not listed" option that reveals a
 * free-text field, which also covers API outages and pre-vPIC vehicles.
 */
import { isModelExcluded, MODEL_NOT_LISTED } from '../../shared/schema'
import { clearFieldError } from './validation'

const MODELS_API = 'https://vpic.nhtsa.dot.gov/api/vehicles/GetModelsForMakeYear'
const YEAR_MIN = 1950
const NOT_LISTED_LABEL = 'My model isn’t listed…'

export interface VehicleCascade {
  /** Apply a VIN decode: select year + make, load models, pick the model. */
  applyVinDecode(year: string, makeCode: string, modelName: string): Promise<void>
}

function option(value: string, label: string): HTMLOptionElement {
  const el = document.createElement('option')
  el.value = value
  el.textContent = label
  return el
}

export function initVehicleCascade(): VehicleCascade {
  const year = document.getElementById('year') as HTMLSelectElement
  const make = document.getElementById('make') as HTMLSelectElement
  const model = document.getElementById('model') as HTMLSelectElement
  const modelOtherWrap = document.getElementById('modelOtherWrap') as HTMLElement
  const modelOther = document.getElementById('modelOther') as HTMLInputElement
  const leafNotice = document.getElementById('leafNotice') as HTMLElement

  // --- Year options: next model year down to YEAR_MIN ---
  const maxYear = new Date().getFullYear() + 1
  year.append(...Array.from({ length: maxYear - YEAR_MIN + 1 }, (_, i) => option(String(maxYear - i), String(maxYear - i))))

  let modelsToken = 0 // ignore stale async responses

  function setModelOptions(models: string[], keep = ''): void {
    // Excluded models (the Nissan Leaf) never appear in the list. The schema
    // rejects them wherever they are typed too; this keeps the choice honest.
    const offered = models.filter((m) => !isModelExcluded(make.value, m))
    model.replaceChildren(
      option('', 'Select model'),
      ...offered.map((m) => option(m, m)),
      option(MODEL_NOT_LISTED, NOT_LISTED_LABEL),
    )
    model.value = keep && (offered.includes(keep) || keep === MODEL_NOT_LISTED) ? keep : ''
    syncModelOther()
  }

  function syncGates(): void {
    make.disabled = year.value === ''
    model.disabled = make.value === '' || year.value === ''
  }

  function syncModelOther(): void {
    const show = model.value === MODEL_NOT_LISTED
    modelOtherWrap.classList.toggle('hidden', !show)
    if (!show && modelOther.value) {
      modelOther.value = ''
      clearFieldError(modelOther)
    }
    syncExcludedNotice()
  }

  // The "we can't accept this model" notice follows whatever the effective
  // model is — the dropdown value or the "not listed" free text (which is
  // also where a VIN decode of an excluded model lands, since the dropdown
  // never offers it).
  function syncExcludedNotice(): void {
    const effective = model.value === MODEL_NOT_LISTED ? modelOther.value : model.value
    leafNotice.classList.toggle('hidden', !isModelExcluded(make.value, effective))
  }

  async function loadModels(keep = ''): Promise<void> {
    const token = ++modelsToken
    if (!year.value || !make.value) {
      setModelOptions([])
      syncGates()
      return
    }

    // vPIC keys models by make NAME — use the option label ("Mercedes-Benz").
    const makeName = make.selectedOptions[0]?.textContent?.trim() ?? ''
    if (make.value === 'OTHER' || !makeName) {
      setModelOptions([], keep)
      syncGates()
      return
    }

    model.disabled = true
    model.replaceChildren(option('', 'Loading models…'))

    let models: string[] = []
    try {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort('timeout'), 6000)
      const res = await fetch(
        `${MODELS_API}/make/${encodeURIComponent(makeName)}/modelyear/${encodeURIComponent(year.value)}?format=json`,
        { signal: controller.signal },
      )
      clearTimeout(timeout)
      if (res.ok) {
        const data = (await res.json()) as { Results?: { Model_Name?: string }[] }
        models = [...new Set((data.Results ?? []).map((r) => (r.Model_Name ?? '').trim()).filter(Boolean))].sort(
          (a, b) => a.localeCompare(b),
        )
      }
    } catch {
      /* fall through — "not listed" free text remains available */
    }

    if (token !== modelsToken) return // a newer selection superseded this load
    setModelOptions(models, keep)
    syncGates()
  }

  year.addEventListener('change', () => {
    syncGates()
    // Year changes can invalidate the model list — reload, keeping the
    // current model when it exists for the new year too.
    if (make.value) void loadModels(model.value)
    else setModelOptions([])
  })

  make.addEventListener('change', () => {
    void loadModels()
  })

  model.addEventListener('change', syncModelOther)
  modelOther.addEventListener('input', syncExcludedNotice)

  syncGates()

  return {
    async applyVinDecode(decodedYear: string, makeCode: string, modelName: string): Promise<void> {
      if (Array.from(year.options).some((o) => o.value === decodedYear)) {
        year.value = decodedYear
        clearFieldError(year)
      }
      if (Array.from(make.options).some((o) => o.value === makeCode)) {
        make.value = makeCode
        clearFieldError(make)
      }
      syncGates()
      await loadModels()

      if (!modelName) return
      const match = Array.from(model.options).find((o) => o.value.toLowerCase() === modelName.toLowerCase())
      model.value = match ? match.value : MODEL_NOT_LISTED
      if (!match) modelOther.value = modelName // typed-in model stays visible via the wrap
      clearFieldError(model)
      syncModelOther()
    },
  }
}
